import { FileVisibility } from './fileVisibility';

/**
 * The value sent as the `accessScope` query param on the upload-complete call. It
 * supersedes the older isBuilderAsset + isRestrictedToUser + publicAcl flags.
 *
 * FileVisibility uses these same spellings, so the conversion below is an identity
 * map plus two legacy aliases. The separate enum exists to mark which side of the
 * wire a value belongs to, not to translate between two vocabularies.
 */
export enum FileAccessScope {
  USER = 'USER',
  APPLICATION = 'APPLICATION',
  ALL_USERS = 'ALL_USERS',
  PUBLIC = 'PUBLIC',
}

/**
 * Resolves a stored visibility to the scope the backend understands.
 *
 * Mostly identity, so it looks redundant — it is not. It does two things a cast
 * could not:
 *
 *  1. Narrows. FileVisibility includes deprecated values that must never reach the
 *     API; this map is the only door between the two, so RESTRICTED / ONLY_ME are
 *     translated rather than passed through. Blocks saved before visibility adopted
 *     the backend spelling still hold them — deleting either entry silently
 *     re-scopes live files.
 *  2. Stays exhaustive. `Record<FileVisibility, FileAccessScope>` means a new
 *     visibility fails to compile until someone states which scope it maps to,
 *     instead of leaking an unknown value onto the wire.
 */
export const FILE_VISIBILITY_TO_ACCESS_SCOPE: Record<FileVisibility, FileAccessScope> = {
  [FileVisibility.USER]: FileAccessScope.USER,
  [FileVisibility.APPLICATION]: FileAccessScope.APPLICATION,
  [FileVisibility.ALL_USERS]: FileAccessScope.ALL_USERS,
  [FileVisibility.PUBLIC]: FileAccessScope.PUBLIC,

  [FileVisibility.RESTRICTED]: FileAccessScope.ALL_USERS,
  [FileVisibility.ONLY_ME]: FileAccessScope.USER,
};
